import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Loader2, Download, Trash2, Power, PowerOff, Settings } from 'lucide-react';
import { toast } from 'sonner';
import {
  adminListDownloadAssets, adminCreateDownloadAsset, adminPatchDownloadAsset,
  adminSetDownloadAssetStatus, adminDeleteDownloadAsset, friendlyError,
} from '@/lib/api';

const PROVIDERS = [
  { value: 'bunny_storage', label: 'Bunny Storage (con Token Auth) — recomendado' },
  { value: 'external', label: 'External (URL privada directa)' },
  { value: 's3', label: 'Amazon S3 — pendiente' },
  { value: 'r2', label: 'Cloudflare R2 — pendiente' },
  { value: 'ionos', label: 'IONOS — pendiente' },
];
const STATUSES = [
  { value: 'preparing', label: 'En preparación' },
  { value: 'active', label: 'Activa' },
  { value: 'disabled', label: 'Desactivada' },
];

/**
 * Fase 06D · Diálogo para gestionar los download_assets de un
 * source (event o video). Permite crear, editar, activar/desactivar y
 * eliminar los archivos descargables referenciados en el proveedor externo.
 *
 * Props:
 *   open, onOpenChange
 *   sourceType: 'event' | 'video'
 *   sourceId: string
 *   sourceTitle: string (para header)
 */
export function AdminDownloadAssetDialog({ open, onOpenChange, sourceType, sourceId, sourceTitle }) {
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [editing, setEditing] = useState(null); // null | {id?, ...}

  const reload = async () => {
    if (!open || !sourceType || !sourceId) return;
    setLoading(true);
    try {
      const data = await adminListDownloadAssets({ source_type: sourceType, source_id: sourceId });
      setAssets(data?.items || []);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [open, sourceType, sourceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (payload) => {
    try {
      if (editing?.id) {
        await adminPatchDownloadAsset(editing.id, payload);
        toast.success('Asset actualizado.');
      } else {
        await adminCreateDownloadAsset({ source_type: sourceType, source_id: sourceId, ...payload });
        toast.success('Asset creado.');
      }
      setEditing(null);
      await reload();
    } catch (err) { toast.error(friendlyError(err)); }
  };
  const handleStatus = async (asset, next) => {
    try {
      await adminSetDownloadAssetStatus(asset.id, next);
      toast.success('Estado actualizado.');
      await reload();
    } catch (err) { toast.error(friendlyError(err)); }
  };
  const handleDelete = async (asset) => {
    if (!window.confirm('¿Eliminar este archivo descargable? El acceso ya concedido no se verá afectado por esta acción.')) return;
    try {
      await adminDeleteDownloadAsset(asset.id);
      toast.success('Asset eliminado.');
      await reload();
    } catch (err) { toast.error(friendlyError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="admin-download-asset-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-4 w-4 text-[var(--ns-gold-700)]" />
            Gestionar descarga — {sourceType === 'event' ? 'Partido' : 'Contenido'}
          </DialogTitle>
          {sourceTitle ? (
            <p className="text-xs text-[var(--ns-text-muted)]">{sourceTitle}</p>
          ) : null}
        </DialogHeader>

        {editing ? (
          <AssetForm
            initial={editing}
            onCancel={() => setEditing(null)}
            onSave={handleSave}
          />
        ) : (
          <div className="flex flex-col gap-3">
            {loading ? (
              <p className="text-xs text-[var(--ns-text-muted)] text-center py-6">Cargando…</p>
            ) : assets.length === 0 ? (
              <Card className="p-6 text-center rounded-xl border border-dashed border-[var(--ns-border-strong)] bg-[var(--ns-bg-2)]/40">
                <p className="text-sm text-[var(--ns-text-muted)]">Aún no hay archivo descargable asociado.</p>
              </Card>
            ) : (
              <ul data-testid="admin-download-asset-list" className="flex flex-col gap-2">
                {assets.map((a) => (
                  <li key={a.id} data-testid={`admin-download-asset-${a.id}`} className="p-3 rounded-xl border border-[var(--ns-border)] bg-white flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[var(--ns-text)] truncate">
                          {a.title || a.file_name || 'Archivo descargable'}
                        </p>
                        <p className="text-[11px] text-[var(--ns-text-subtle)] truncate">
                          {a.provider} · {a.provider_path || <em className="text-red-600">sin path</em>}
                        </p>
                      </div>
                      <StatusBadge status={a.status} configured={a.is_provider_configured} />
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setEditing(a)} data-testid={`admin-download-asset-edit-${a.id}`}>
                        <Settings className="h-3 w-3 mr-1" /> Editar
                      </Button>
                      {a.status !== 'active' ? (
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => handleStatus(a, 'active')} data-testid={`admin-download-asset-activate-${a.id}`}>
                          <Power className="h-3 w-3 mr-1" /> Activar
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => handleStatus(a, 'disabled')} data-testid={`admin-download-asset-disable-${a.id}`}>
                          <PowerOff className="h-3 w-3 mr-1" /> Desactivar
                        </Button>
                      )}
                      {a.status !== 'preparing' ? (
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => handleStatus(a, 'preparing')} data-testid={`admin-download-asset-preparing-${a.id}`}>
                          Dejar en preparación
                        </Button>
                      ) : null}
                      <Button size="sm" variant="ghost" className="h-8 text-xs text-red-600 hover:bg-red-50" onClick={() => handleDelete(a)} data-testid={`admin-download-asset-delete-${a.id}`}>
                        <Trash2 className="h-3 w-3 mr-1" /> Eliminar
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <DialogFooter>
              <Button
                onClick={() => setEditing({})}
                data-testid="admin-download-asset-add"
                className="bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold border-0"
              >
                Añadir archivo descargable
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({ status, configured }) {
  if (!configured) {
    return (
      <Badge className="text-[10px] uppercase bg-red-100 text-red-800">
        <AlertCircle className="h-3 w-3 mr-1" /> Proveedor no seguro
      </Badge>
    );
  }
  if (status === 'active') return <Badge className="text-[10px] uppercase bg-emerald-100 text-emerald-800">Activa</Badge>;
  if (status === 'preparing') return <Badge className="text-[10px] uppercase bg-amber-100 text-amber-800">Preparación</Badge>;
  return <Badge className="text-[10px] uppercase bg-gray-200 text-gray-700">Desactivada</Badge>;
}

function AssetForm({ initial, onCancel, onSave }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    provider: initial?.provider || 'bunny_storage',
    provider_path: initial?.provider_path || '',
    provider_asset_id: initial?.provider_asset_id || '',
    provider_source_reference: initial?.provider_source_reference || '',
    title: initial?.title || '',
    description: initial?.description || '',
    file_name: initial?.file_name || '',
    file_format: initial?.file_format || '',
    file_size_bytes: initial?.file_size_bytes ?? '',
    duration_seconds: initial?.duration_seconds ?? '',
    status: initial?.status || 'preparing',
    download_product_id: initial?.download_product_id || '',
  });

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    // Normalizar numéricos
    payload.file_size_bytes = payload.file_size_bytes === '' ? null : Number(payload.file_size_bytes);
    payload.duration_seconds = payload.duration_seconds === '' ? null : Number(payload.duration_seconds);
    // Vaciar strings
    ['provider_source_reference', 'provider_asset_id', 'description', 'title', 'file_name', 'file_format', 'download_product_id']
      .forEach((k) => { if (payload[k] === '') payload[k] = null; });
    setSaving(true);
    try { await onSave(payload); } finally { setSaving(false); }
  };

  const providerNeedsSecureConfig = useMemo(
    () => ['s3', 'r2', 'ionos'].includes(form.provider),
    [form.provider],
  );

  return (
    <form onSubmit={submit} className="flex flex-col gap-3" data-testid="admin-download-asset-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Proveedor" required>
          <Select value={form.provider} onValueChange={(v) => setForm((f) => ({ ...f, provider: v }))}>
            <SelectTrigger data-testid="admin-download-asset-provider" className="h-10 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>{PROVIDERS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Estado">
          <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
            <SelectTrigger data-testid="admin-download-asset-status" className="h-10 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>

      {providerNeedsSecureConfig ? (
        <div className="p-2 rounded-lg bg-red-50 border border-red-200 text-[11px] text-red-800">
          Este proveedor aún no está implementado. El asset se guardará pero no permitirá descargas seguras. Cambia a Bunny Storage para activar la descarga.
        </div>
      ) : null}

      <Field label="provider_path (Bunny: ruta estable, sin URL ni slash inicial)" required={form.provider === 'bunny_storage'}>
        <Input
          value={form.provider_path}
          onChange={(e) => setForm((f) => ({ ...f, provider_path: e.target.value }))}
          placeholder="vod/2026/mi-partido.mp4"
          data-testid="admin-download-asset-provider-path"
          className="h-10 bg-white font-mono text-xs"
        />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Nombre del archivo (Content-Disposition)">
          <Input
            value={form.file_name}
            onChange={(e) => setForm((f) => ({ ...f, file_name: e.target.value }))}
            placeholder="partido-completo.mp4"
            data-testid="admin-download-asset-file-name" className="h-10 bg-white"
          />
        </Field>
        <Field label="Formato">
          <Input
            value={form.file_format}
            onChange={(e) => setForm((f) => ({ ...f, file_format: e.target.value }))}
            placeholder="mp4" data-testid="admin-download-asset-file-format" className="h-10 bg-white"
          />
        </Field>
        <Field label="Tamaño aprox. (bytes)">
          <Input type="number" min={0}
            value={form.file_size_bytes}
            onChange={(e) => setForm((f) => ({ ...f, file_size_bytes: e.target.value }))}
            data-testid="admin-download-asset-size" className="h-10 bg-white"
          />
        </Field>
        <Field label="Duración aprox. (segundos)">
          <Input type="number" min={0}
            value={form.duration_seconds}
            onChange={(e) => setForm((f) => ({ ...f, duration_seconds: e.target.value }))}
            data-testid="admin-download-asset-duration" className="h-10 bg-white"
          />
        </Field>
      </div>

      <Field label="Título (opcional)">
        <Input
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder="Descarga oficial" data-testid="admin-download-asset-title" className="h-10 bg-white"
        />
      </Field>

      <Field label="download_product_id (05B) — vincula la lógica comercial">
        <Input
          value={form.download_product_id}
          onChange={(e) => setForm((f) => ({ ...f, download_product_id: e.target.value }))}
          placeholder="UUID del download_product" data-testid="admin-download-asset-product"
          className="h-10 bg-white font-mono text-xs"
        />
      </Field>

      <DialogFooter className="mt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving} data-testid="admin-download-asset-cancel">Cancelar</Button>
        <Button type="submit" disabled={saving} data-testid="admin-download-asset-save"
          className="bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold border-0">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (initial?.id ? 'Guardar cambios' : 'Crear asset')}
        </Button>
      </DialogFooter>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs font-medium">
        {label}{required ? <span className="text-red-600 ml-0.5">*</span> : null}
      </Label>
      {children}
    </div>
  );
}
